import { SagaIterator } from 'redux-saga';
import { all, call, CallEffect, fork, put, select, takeEvery, takeLatest, Effect, spawn, PutEffect } from 'redux-saga/effects';
import { conf, Relationship, RelPoint, RelSide, RelType, Field, FieldType } from 'src/config';
import { httpGet, httpPost, httpDelete, httpPatch, httpPut, ApiResponse, CallResult } from 'src/api';
import dayjs from "src/dayjs";
import * as constants from './constants';
import * as types from './types';
import * as actions from './actions';
import * as selectors from './selectors';
import * as utils from './utils';
import _, { Dictionary } from 'lodash';
import { addSnackbar } from 'src/domains/global/actions';
import {
    getMorph,
    getRelInfo,
    isRoot,
    isPersistent,
    isTransient,
    isTemp,
    getPorts,
    nextTransientId,
    getOrigAndNewIds,
    getOrigAndRemovedIds,
    isEqualLocator,
    toInitializedPorts,
    toInitializedPortsMixed,
    toOppositeVector
} from './utils';

function* startWatchers(): SagaIterator {
    yield takeLatest(constants.START_FETCH, startFetchWorker);
    yield takeLatest(constants.LINK, linkWorker);
    yield takeLatest(constants.UNLINK, unLinkWorker);
    yield takeLatest(constants.LINK_HARD, linkHardWorker);
    yield takeLatest(constants.UNLINK_HARD, unlinkHardWorker);
    yield takeLatest(constants.DELETE_REL, deleteRelatedWorker);
    yield takeLatest(constants.DELETE, deleteWorker);
    yield takeLatest(constants.SUBMIT, submitWorker);
    yield takeLatest(constants.CANCEL, cancelWorker);
    yield takeLatest(constants.CREATE_TEMP, createTempWorker);
    yield takeLatest(constants.CREATE_NEW, createNewWorker);
}

const relSide = (inverse: boolean) => inverse ? 'backReferenceName' : 'referenceName';
const confMethod = (inverse: boolean) => inverse ? 'getInbound' : 'getOutbound';
const resName = (rel: Relationship, inverse: boolean) => inverse ? rel.resource : rel.related;

/**
 * --- FETCH ------------------------------
 * 
 */
function* startFetchWorker(payload: types.StartFetchAction) {
    const {
        request: locator,
        full,
        force,
    } = payload;

    yield doFetch(locator, full, force);
}

function* doFetch(request: types.FetchRequest, full: boolean, force: boolean) {
    const { name, id } = request;
    const entity: types.Entity = yield select(selectors.SelectEntity(request));

    if (!force && entity && entity.full) {
        return;
    }

    const response: ApiResponse = yield call(
        httpGet,
        `/${name}/{id}`,
        { id },
        {}
    );
    const body: any = response.json;
    yield initializeEntity(request, body, full);
}

const setFields = (entity: types.Entity, resource: string, body: any) => {
    const fields: Dictionary<Field> = conf.getFields(resource);
    _.keys(fields)
        .filter(fieldName => body[fieldName] !== undefined)
        .forEach(fieldName => {
            let fieldConfig = fields[fieldName];
            let rawValue = body[fieldName];
            let value = rawValue;

            if (rawValue != null && fieldConfig.type == FieldType.DATE_TIME) {
                value = dayjs.utc(rawValue).local().format("YYYY-MM-DDTHH:mm:ss.SSSZZ");
            }
            
            entity.attrs[fieldName] = value;
        });

    // Initialize isLocked
    const entityLocked = _.get(entity?.attrs, '_locked');
    entity.locked = entityLocked != null ? entityLocked as boolean : false;
}

// --- Lazily initialize entity (if not in session) -------
function* initializeEntityLazy(
    locator: types.Locator,
    body: any,
    full?: boolean,
) {
    const entity: types.Entity = yield select(selectors.SelectEntity(locator));
    if (entity === undefined) {
        yield initializeEntity(locator, body, full);
    }
}

// --- Hydrate and put into store -------
function* initializeEntity(
    locator: types.Locator,
    body: any,
    full?: boolean,
) {
    const entity: types.Entity = utils.entity();

    const morph = getMorph(body);
    if (morph !== locator.name) {
        entity.morph = morph;
    }

    yield hydrateEntity(locator, entity, body, full);
    yield put(actions.setEntity(locator, entity));
}

// --- Hydrate ------------------------
function* hydrateEntity(
    locator: types.Locator,
    entity: types.Entity,
    body: any,
    full?: boolean,
) {
    // --- Simple Fields ---
    setFields(entity, entity.morph ?? locator.name, body);

    if (full) {
        // --- Many -> 1
        yield hydrate_N(RelType.MANY_TO_ONE, locator, entity, true);
        yield hydrate_1(RelType.MANY_TO_ONE, locator, entity, false);

        // --- 1 -> 1
        yield hydrate_1(RelType.ONE_TO_ONE, locator, entity, false);
        yield hydrate_1(RelType.ONE_TO_ONE, locator, entity, true);

        // --- Many -> Many
        yield hydrate_N(RelType.MANY_TO_MANY, locator, entity, false);
        yield hydrate_N(RelType.MANY_TO_MANY, locator, entity, true);

        entity.full = true;
    }
    entity.values = _.cloneDeep(entity.attrs);
}

// --- [1] Single entity
function* hydrate_1(
    relType: RelType.MANY_TO_ONE | RelType.ONE_TO_ONE,
    locator: types.Locator,
    entity: types.Entity,
    inverse: boolean

): Generator<any, any, any> {
    if (relType === RelType.MANY_TO_ONE && inverse) {
        throw new Error('incorrect method usage');
    }

    const { name, id } = locator;
    const entities: types.Entities = yield select(selectors.SelectEntities());
    const rels: Relationship[] = conf[confMethod(inverse)](name, relType);
    const calls: CallEffect[] = [];

    for (const rel of rels) {
        let callEffect: CallEffect<ApiResponse> = call(
            httpGet,
            `/${name}/{id}/${rel[relSide(inverse)]}`,
            { id },
            {}
        );
        calls.push(callEffect);
    }

    const responses: Array<ApiResponse> = yield all(calls);

    for (let i = 0; i < responses.length; i++) {
        const rel = rels[i];
        const res = responses[i];
        const resourceName: string = resName(rel, inverse);
        let relatedId: types.ID | null = null;

        if (res.status === 200) {
            const { json } = res;

            relatedId = json['_id'];
            yield initializeEntityLazy(
                {
                    name: resourceName,
                    id: json['_id']
                },
                json
            );
        } else {
            const relatedTransientId = _.keys(entities[resourceName])
                .filter(key => isTransient(key))
                .find(key => {
                    const ids = entities[resourceName][key].values[rel[relSide(!inverse)]] as types.ID[];
                    return ids?.indexOf(id) > -1;
                });

            if (relatedTransientId != null) {
                relatedId = relatedTransientId;
            }
        }

        if (relatedId != null) {
            entity.attrs[rel[relSide(inverse)]] = relatedId;
        }
    }
}

// --- [N] Collection --------------------
function* hydrate_N(
    relType: RelType.MANY_TO_ONE | RelType.MANY_TO_MANY,
    locator: types.Locator,
    entity: types.Entity,
    inverse: boolean

): Generator<any, any, any> {
    if (relType === RelType.MANY_TO_ONE && !inverse) {
        throw new Error('incorrect method usage');
    }
    const { name, id } = locator;
    const entities: types.Entities = yield select(selectors.SelectEntities());
    const rels: Relationship[] = conf[confMethod(inverse)](name, relType);
    const calls: CallEffect[] = [];

    for (const rel of rels) {
        let callEffect: CallEffect<ApiResponse> = call(
            httpGet,
            `/${name}/{id}/${rel[relSide(inverse)]}`,
            { id },
            {}
        );
        calls.push(callEffect);
    }

    const responses: Array<ApiResponse> = yield all(calls);

    for (let i = 0; i < responses.length; i++) {
        const rel = rels[i];
        const res = responses[i];
        const resourceName: string = resName(rel, inverse);
        const ids: types.ID[] = [];

        if (res.status === 200) {
            const morphsDict: Dictionary<any[]> = res.json['_embedded'];
            const morphs = _.keys(morphsDict);

            for (const morph of morphs) {
                const items = morphsDict[morph] as any[];

                // might be undefined due to polymorphism
                if (items) {
                    for (const item of items) {
                        ids.push(item['_id'].toString());
                        yield initializeEntityLazy(
                            {
                                name: resourceName,
                                id: item['_id'],
                            },
                            item
                        );
                    }
                }
            }
        }

        const relatedTransientIds = _.keys(entities[resourceName])
            .filter(key => isTransient(key))
            .filter(key => entities[resourceName][key].values[rel[relSide(!inverse)]] == id);

        if (relatedTransientIds.length > 0) {
            ids.push(...relatedTransientIds);
        }

        if (ids.length > 0) {
            entity.attrs[rel[relSide(inverse)]] = ids;
        }
    }
}

// --- LINK ------------------------
function* linkWorker(payload: types.LinkAction) {
    const { locator, vector, ids } = payload;

    const { name, many } = getRelInfo(vector);

    const state: types.Explorer = yield select(selectors.SelectState());
    const entities: types.Entities = state.entities;

    for (const id of ids) {
        const inSession = _.get(entities, [name, id]) !== undefined;

        if (!isTransient(id) && !inSession) {
            yield doFetch(
                {
                    name,
                    id,
                },
                false,
                false
            );
        }
    }

    const origAndNewIds = getOrigAndNewIds(entities, locator, vector, ids);
    const updatedIds: types.ID[] = many ? _.uniq([
        ...origAndNewIds['original'],
        ...origAndNewIds['new']
    ]) : [
        origAndNewIds['new'][0]
    ];

    yield put(actions.setVectorAction(locator, vector, updatedIds));

    // Handle the opposite side of relationship.
    for (const id of ids) {
        const state: types.Explorer = yield select(selectors.SelectState());
        const entities: types.Entities = state.entities;
        const oppLocator: types.Locator = { name, id };
        const oppVector: RelPoint = toOppositeVector(vector);
        const oppOrigAndNewIds = getOrigAndNewIds(entities, oppLocator, oppVector, [locator.id]);
        const oppUpdatedIds: types.ID[] = _.uniq([
            ...oppOrigAndNewIds['original'],
            ...oppOrigAndNewIds['new']
        ]);

        yield put(actions.setVectorAction(oppLocator, oppVector, oppUpdatedIds));
    }
}

function* linkHardWorker(payload: types.LinkHardAction) {
    const { locator, vector, ids, vector: { rel, side } } = payload;
    const { direct, prop, isM2M } = getRelInfo(vector);

    const urlIds: string[] = isM2M || direct ? [locator.id] : ids;
    const bodyIds: string[] = isM2M || direct ? ids : [locator.id];

    const urlResource: string = isM2M ? locator.name : rel.resource;
    const bodyResource: string = urlResource === rel.resource ? rel.related : rel.resource;
    const attr: string = isM2M ? prop : rel.referenceName;

    for (const urlId of urlIds) {
        const url = `/${urlResource}/${urlId}/${attr}`;

        const putBody = bodyIds.reduce((acc, id, index, arr) => {
            return acc + `/${bodyResource}/${id}` + (index < arr.length - 1 ? '\n' : '');
        }, '');

        const response: ApiResponse = yield call(
            httpPut,
            url,
            {},
            {},
            putBody
        );

        if (response.status !== 204) {
            throw new Error('Error');
        }
    }
}

// --- UN-LINK ------------------------
function* unLinkWorker(payload: types.UnLinkAction) {
    const { locator, vector, ids, vector: { rel, side } } = payload;
    const { direct, name, prop, isM2M, isM21 } = getRelInfo(vector);

    const state: types.Explorer = yield select(selectors.SelectState());
    const entities: types.Entities = state.entities;

    for (const id of ids) {
        const inSession = _.get(entities, [name, id]) !== undefined;

        if (!isTransient(id) && !inSession) {
            yield doFetch(
                {
                    name,
                    id,
                },
                false,
                false,
            );
        }
    }

    const origAndRemovedIds = getOrigAndRemovedIds(entities, locator, vector, ids);
    const updatedIds: types.ID[] = [...origAndRemovedIds['original']];
    _.remove(updatedIds, id => origAndRemovedIds['removed'].includes(id));

    yield put(actions.setVectorAction(locator, vector, updatedIds));

    // Handle the opposite side of relationship.
    for (const id of ids) {
        const state: types.Explorer = yield select(selectors.SelectState());
        const entities: types.Entities = state.entities;
        const oppLocator: types.Locator = { name, id };
        const oppVector: RelPoint = toOppositeVector(vector);
        const oppOrigAndRemovedIds = getOrigAndRemovedIds(entities, oppLocator, oppVector, [locator.id]);
        const oppUpdatedIds: types.ID[] = [...oppOrigAndRemovedIds['original']];
        _.remove(oppUpdatedIds, id => oppOrigAndRemovedIds['removed'].includes(id));

        yield put(actions.setVectorAction(oppLocator, oppVector, oppUpdatedIds));
    }
}

function* unlinkHardWorker(payload: types.UnLinkHardAction) {
    const { locator, vector, ids, vector: { rel, side } } = payload;
    const { direct, name, prop, isM2M, isM21, is121, many } = getRelInfo(vector);

    const urlIds: string[] = isM2M || direct ? [locator.id] : ids;
    const bodyIds: string[] = isM2M || direct ? ids : [locator.id];

    const urlResource: string = isM2M ? locator.name : rel.resource;
    const bodyResource: string = urlResource === rel.resource ? rel.related : rel.resource;
    const attr: string = isM2M ? prop : rel.referenceName;

    for (const urlId of urlIds) {
        const url = `/${urlResource}/${urlId}/${attr}`;

        const putBody = bodyIds.reduce((acc, id, index, arr) => {
            return acc + `/${bodyResource}/${id}` + (index < arr.length - 1 ? '\n' : '');
        }, '');

        let response: ApiResponse;

        if (isM21) {
            response = yield call(
                httpDelete,
                url,
                {},
                {},
            )

        } else {
            response = yield call(
                httpPut,
                url,
                {},
                {},
                putBody
            )
        }

        if (response.status !== 204) {
            throw new Error('Error');
        }
    }
}

function* deleteRelatedWorker(payload: types.DeleteRelatedAction) {
    const { locator, vector, ids } = payload;
    const { direct, name, prop, isM2M, isM21 } = getRelInfo(vector);

    const entity: types.Entity = yield select(selectors.SelectEntity(locator));
    const origIds: types.ID[] = entity.values[prop] as types.ID[];

    const deletedIds: types.ID[] = [];
    const calls: CallEffect[] = [];

    for (const id of ids) {
        let callEffect: CallEffect<ApiResponse> = call(
            httpDelete,
            `/${name}/${id}`,
            {},
            {}
        );
        calls.push(callEffect);
    }

    const responses: Array<ApiResponse> = yield all(calls);

    for (let i = 0; i < responses.length; i++) {
        const id = ids[i];
        const res = responses[i];
        if (res.status !== 204) {
            yield put(addSnackbar('delete-error', `Could not delete resource: /${name}/${id}`));
        } else {
            deletedIds.push(id)
        }
    }

    for (let i = 0; i < deletedIds.length; i++) {
        yield put(actions.removeAction({ name, id: deletedIds[i] }));
    }
}

function* deleteWorker(payload: types.DeleteAction) {
    const { locator: { name, id }, onComplete } = payload;
    const response: ApiResponse = yield call(
        httpDelete,
        `/${name}/{id}`,
        { id },
        {},
    );

    if (onComplete != null) {
        onComplete(response.status === 204 ? 'success' : 'error');
    }
}

/**
 * ========================= SUBMIT ====================================================================================================
 */
function* submitWorker(payload: types.SubmitAction) {
    let { locator: { name, id }, locator, onComplete } = payload;

    const state: types.Explorer = yield select(selectors.SelectState());
    const entities: types.Entities = state.entities;
    const entity: types.Entity = _.cloneDeep(entities[name][id]);
    const rootId: string = state.stack[0].locator.id;

    if (isPersistent(id) && id === rootId) { // -------------------------------------- Persistent entity -------------------------------
        const response: ApiResponse = yield update(entity, name);
        if (response == null) {
            yield put(actions.pop());
            if (onComplete != null) {
                onComplete('success');
            }
        }
        // else {
        //     yield put(actions.setErrors(
        //         locator,
        //         response.json as types.Errors
        //     ));
        // }

    } else if (isTransient(id) || isPersistent(id)) { // ----------------------------- Transient entity --------------------------------
        yield put(actions.pop());

    } else if (isTemp(id)) { // ------------------------------------------------------ Temp entity - create new entity -----------------
        // const refPort: types.RelPort | null = parseId(id); // referer
        // if (refPort === null) {
        //     throw Error('Cannot identify port for temp id: ' + id);
        // }

        const newId: string = nextTransientId(entities[name]);

        yield put(
            actions.replaceId(
                locator,
                newId,
            )
        );

        const newEntity: types.Entity = yield select(selectors.SelectEntity({ name, id: newId }));
        const ports: types.RelPort[] = getPorts(newEntity, name);
        for (const port of ports) {
            yield put(actions.link(port.locator, toOppositeVector(port.relPoint), [newId]));
        }

        yield put(actions.pop());

    } else if (isRoot(id)) { // ------------------------------------------------------ Temp root (new entity and root) -----------------
        const result: CallResult | null = yield create(entity, name);
        if (result == null) {
            yield put(actions.pop());
            if (onComplete != null) {
                onComplete('success');
            }
        }

    } else { // ------------------------------------------------------------------------------------------------------------------------
        throw new Error('Cannot qualify id: ' + id);
    }
}

/**
 * ========================= CANCEL ====================================================================================================
 */
function* cancelWorker(payload: types.CancelAction) {
    let { locator: { name, id }, locator, onComplete } = payload;

    const state: types.Explorer = yield select(selectors.SelectState());
    const entity: types.Entity = _.cloneDeep(state.entities[name][id]);
    const rootId: string = state.stack[0].locator.id;

    if (isPersistent(id) && id === rootId) { // -------------------------------------- Persistent entity -------------------------------


    } else if (isTransient(id) || isPersistent(id)) { // ----------------------------- Transient entity --------------------------------


    } else if (isTemp(id)) { // ------------------------------------------------------ Temp entity - create new entity -----------------
        yield put(actions.removeAction(locator));

    } else if (isRoot(id)) { // ------------------------------------------------------ Temp root (new entity and root) -----------------
        yield put(actions.removeAction(locator));

    } else { // ------------------------------------------------------------------------------------------------------------------------
        throw new Error('Cannot qualify id: ' + id);
    }

    yield put(actions.pop());

    if (onComplete != null) {
        onComplete('abort');
    }
}

/**
 * ===== CREATE =====================================================================================================
 */
function* create(entity: types.Entity, name: string, visited: types.Locator[] = []): Generator<any, any, any> {
    const entities: types.Entities = yield select(selectors.SelectEntities());

    if (entity.morph) {
        name = entity.morph;
    }

    // outM21 (persistent) are taken care of
    const body = buildCreateBody(entity, name, entities)

    const response: ApiResponse = yield call(
        httpPost,
        `/${name}`,
        {},
        {},
        body
    );

    const tempId = entity.values['_id'] as types.ID;
    if (response.status == 400) {
        yield put(actions.setErrors(
            { name, id: tempId },
            response.json as types.Errors
        ));
    } else if (response.status !== 201) {
        yield put(actions.setErrors(
            { name, id: tempId },
            { '/': [{ message: 'Error' }] }
        ));
    } else {
        yield put(actions.setErrors(
            { name, id: tempId },
            {}
        ));
    }

    if (response.status == 400 || response.status !== 201) {
        return {};
    }

    const persistentId = response.json['_id'].toString();

    yield put(
        actions.replaceId(
            {
                name,
                id: tempId
            },
            persistentId
        )
    );

    const thisLocator = {
        name,
        id: persistentId
    };
    visited.push(thisLocator);

    const vectors: RelPoint[] = conf.getVectors(name);

    for (const vector of vectors) {
        const errors: CallResult | null = yield handleRel(
            entity,
            thisLocator,
            vector,
            visited,
        );
        if (errors != null) {
            return {};
        }
    }

    return null;
}

/**
 * ===== HANDLE RELATIONSHIP =============================================================================
 */
function* handleRel(entity: types.Entity, locator: types.Locator, relPoint: RelPoint, visited: types.Locator[]) {
    const ports: utils.InitializedPorts = utils.toInitializedPorts(relPoint, entity);
    let errors: CallResult | null;

    for (let port of ports['transient']) {
        const relEntity: types.Entity = yield select(selectors.SelectEntity(port.locator));
        if (relEntity != null) { // transient entity might not be found anymore because it has become persistent.
            errors = yield create(relEntity, port.locator.name, visited);
            if (errors != null) {
                return {};
            }
        }
    }

    const persistentPorts = ports['persistent']
        .filter(port => visited.find(locator => isEqualLocator(port.locator, locator)) == null);

    for (let port of persistentPorts) {
        const relEntity: types.Entity = yield select(selectors.SelectEntity(port.locator));
        errors = yield update(relEntity, port.locator.name, visited);
        if (errors != null) {
            return {};
        }
    }

    const state: types.Explorer = yield select(selectors.SelectState());
    const entities: types.Entities = state.entities;

    const origAndNewIds = getOrigAndNewIds(entities, locator, relPoint);
    const newPersistentIds = origAndNewIds['new'].filter(id => isPersistent(id));
    const newTransientIds = origAndNewIds['new'].filter(id => isTransient(id));

    const origAndRemovedIds = getOrigAndRemovedIds(entities, locator, relPoint);
    const removedPersistentIds = origAndRemovedIds['removed'].filter(id => isPersistent(id));
    const removedTransientIds = origAndRemovedIds['removed'].filter(id => isTransient(id));

    // if (newTransientIds.length > 0) {
    //     yield put(actions.link(
    //         locator,
    //         relPoint,
    //         newTransientIds,
    //     ));
    // }

    // if (removedTransientIds.length > 0) {
    //     yield put(actions.unlink(
    //         locator,
    //         relPoint,
    //         removedTransientIds,
    //     ));
    // }

    if (newPersistentIds.length > 0) {
        yield put(actions.linkHard(
            locator,
            relPoint,
            newPersistentIds,
        ));
    }

    if (removedPersistentIds.length > 0) {
        yield put(actions.unlinkHard(
            locator,
            relPoint,
            removedPersistentIds,
        ));
    }

    return null;
}

/**
 * ===== UPDATE =====================================================================================================
 */
function* update(entity: types.Entity, name: string, visited: types.Locator[] = []): Generator<any, any, any> {
    const entities: types.Entities = yield select(selectors.SelectEntities());
    const id: types.ID = (entity.attrs['_id'] || entity.values['_id']) as types.ID;

    if (id == null) {
        throw new Error('Cannot update transient entity.');
    }

    const patch = buildPatchBody(entity, name, entities);

    if (patch.length > 0) {
        const response: ApiResponse = yield call(
            httpPatch,
            `/${name}/{id}`,
            { id },
            {},
            patch
        );

        if (response.status == 400) {
            yield put(actions.setErrors(
                { name, id },
                response.json as types.Errors
            ));
        } else if (response.status != 200) {
            yield put(actions.setErrors(
                { name, id },
                { '/': [{ message: 'Error' }] }
            ));
        } else {
            yield put(actions.setErrors(
                { name, id },
                {}
            ));
        }

        if (response.status == 400 || response.status != 200) {
            return {};
        }
    }
    visited.push({ name, id });

    const vectors: RelPoint[] = conf.getVectors(name);

    for (const vector of vectors) {
        const errors: CallResult | null = yield handleRel(
            entity,
            {
                name,
                id
            },
            vector,
            visited,
        );
        if (errors != null) {
            return {};
        }
    }

    return null;
}

const buildPatchBody: (
    entity: types.Entity,
    name: string,
    entities: types.Entities,
    path?: string,
    patch?: types.UpdateOp[],

) => types.UpdateOp[] = (
    entity,
    name,
    entities,
    path = '',
    patch = [],

) => {
        const fields = conf.getEditableFields(name);

        _.keys(fields)
            .reduce((patch, fldName) => {
                const init = entity.attrs[fldName];
                const val = entity.values[fldName];
                if (val !== init && val !== undefined) {
                    patch.push({
                        path: path + '/' + fldName,
                        op: types.UpdateOpType.Replace,
                        value: val
                    });
                }
                return patch;
            },
                patch
            );

        return patch;
    }


/**
 * Build Create Body JSON (recursive).
 */
const buildCreateBody: (
    entity: types.Entity,
    name: string,
    entities: types.Entities,
    backRef?: types.BackRef,

) => any = (
    entity,
    name,
    entities,
    backRef,

) => {
        const body: Dictionary<types.Value> = {};
        const fields = conf.getEditableFields(name);
        _.keys(fields)
            .reduce((body, fldName) => {
                const val = entity.values[fldName];
                if (val !== undefined) {
                    body[fldName] = val;
                }
                return body;
            },
                body
            );

        conf.getRels(name, RelType.MANY_TO_ONE, RelSide.SOURCE)
            .reduce((body, rel) => {
                const attrName = rel.referenceName;
                const subName = rel.related;
                const id: types.ID = entity.values[attrName] as types.ID;
                if (id != null && utils.isPersistent(id)) {
                    body[attrName] = `/${subName}/${id}`;
                }
                return body;
            },
                body
            );

        if (backRef != null) {
            body[backRef.rel.backReferenceName] = `/${backRef.rel.resource}/${backRef.id}`;
        }
        return body;
    }

function* createTempWorker(payload: types.CreateTempAction) {
    const {
        port: {
            attrName,
            locator: {
                name,
                id
            },
            relPoint: {
                rel,
                side,
                rel: {
                    metaType,
                    related,
                    resource,
                    referenceName,
                    backReferenceName,
                }
            },
            relPoint
        }
    } = payload;

    const tempId = `${name}(${id})/${attrName}`;
    const entity = utils.entity(tempId);
    entity.full = true;

    const tempResName = side === RelSide.SOURCE ? related : resource;

    const many: boolean = (metaType === RelType.MANY_TO_MANY) || (metaType === RelType.MANY_TO_ONE && side === RelSide.SOURCE);

    entity.values[side === RelSide.SOURCE ? backReferenceName : referenceName] = many ? [id] : id;

    // populate contextual rels
    const state: types.Explorer = yield select(selectors.SelectState());
    const { stack } = state;

    const ctxVectors: RelPoint[] = conf.getContextualVectors(tempResName);

    for (const ctxVector of ctxVectors) {
        const { rel, side } = ctxVector;
        const ctxResName = (side === RelSide.SOURCE) ? rel.related : rel.resource;
        const index = _.findLastIndex(stack, si => si.locator.name === ctxResName);
        if (index > -1 && index < stack.length - 1) {
            const many: boolean = (metaType === RelType.MANY_TO_MANY) || (metaType === RelType.MANY_TO_ONE && side === RelSide.TARGET);
            const attr = side == RelSide.SOURCE ? rel.referenceName : rel.backReferenceName;
            const id = stack[index].locator.id;

            // yield put(actions.link(locator, ctxVector, [id]));
            entity.values[attr] = many ? [id] : id;
        }
    }

    const locator: types.Locator = {
        name: tempResName,
        id: tempId,
    };

    yield put(actions.setEntity(locator, entity));
    yield put(actions.pushAction(locator, relPoint));
}

function* createNewWorker(payload: types.CreateNewAction) {
    const { name } = payload;

    const id = 'new:' + name;
    const entity = utils.entity(id);
    entity.full = true;

    yield put(actions.clear());
    yield put(actions.setEntity(
        {
            name,
            id,
        },
        entity)
    );
}

/**
 * Start sagas.
 */
export default function* StartSagas() {
    yield all([fork(startWatchers)]);
}

